import * as React from 'react';
import { IMentionItem, ISearchDto } from 'models/common';
import { MentionsInput, Mention } from 'react-mentions';
import { IPage } from 'models/common';
import { IUserListItem } from 'models/user/userlistitem';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { IFetchDefault } from 'redux/modules/fetch';
import { imgSrcAttributes } from 'helpers/common';

require('./style.css');

interface IProps {
	fetchDefault: IFetchDefault;
	input: any;
	meta?: any;
	inputPlaceholder?: string;
	userId?: string;
	onRequestFailure?: (error: any) => void;
	onMentionAdd?: (id: string, name: string) => void;
	t?: (key: string, props?: any) => string;
}

interface IState {
	inputValue: string;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class UserMentionInput extends React.Component<IProps, IState> {
	private defaultMentionStyle: any = {
		backgroundColor: '#cee4e5',
	};

	private defaultStyle: any = {
		control: {
			backgroundColor: '#fff',
			fontSize: 14,
			fontWeight: 'normal',
			display: 'block',
		},

		highlighter: {
			overflow: 'hidden',
		},

		input: {
			margin: 0,
			height: '100%',
		},

		'&singleLine': {
			control: {
				display: 'inline-block',
				width: 130,
			},

			highlighter: {
				padding: 1,
				border: '1px solid #000',
			},

			input: {
				padding: '8px 0 6px 9px',
				border: '1px solid',
				width: '100%',
			},
		},

		'&multiLine': {
			control: {
				fontFamily: 'monospace',
				border: '1px solid silver',
			},

			highlighter: {
				padding: 9,
			},

			input: {
				padding: 9,
				minHeight: 63,
				outline: 0,
				border: 0,
			},
		},

		suggestions: {
			list: {
				backgroundColor: 'white',
				border: '1px solid rgba(0,0,0,0.15)',
				fontSize: 14,
			},

			item: {
				padding: '5px 15px',
				borderBottom: '1px solid rgba(0,0,0,0.15)',
				'&focused': {
					backgroundColor: '#cee4e5',
				},
			},
		},
	};

	constructor(props) {
		super(props);

		if (props.input.value) {
			this.state = {
				inputValue: props.input.value,
			};
		}

		this.handleChange = this.handleChange.bind(this);
		this.fetchUsers = this.fetchUsers.bind(this);
	}

	public componentDidUpdate(prevProps: IProps) {
		if (prevProps.input.value !== this.props.input.value) {
			this.setState({
				inputValue: this.props.input.value,
			});
		}
	}

	public state = {
		inputValue: '',
	};

	private handleChange(e: any) {
		this.setState({ inputValue: e.target.value });
		this.props.input.onChange(e.target.value);
	}

	private fetchUsers(query, callback) {
		if (query && query.length > 0) {
			const data: ISearchDto = {
				keyword: query,
				currentPage: 1,
				itemsPerPage: 5,
			};

			return fetch(
				this.props.fetchDefault.baseAPI + 'api/User/Mentions',
				Object.assign({}, this.props.fetchDefault.requestInit, {
					method: 'POST',
					body: JSON.stringify(data),
				}),
			)
				.then((res) => {
					if (res.ok) {
						return res.json().then((res: IPage) => {
							const users = res.items.map((user: IUserListItem) => ({
								display: user.name,
								id: JSON.parse(user.entityId).id,
								picture: user.picture,
							}));
							callback(users);
						});
					} else {
						const status: number = res.status;
						const statusText: string = res.statusText;
						return res
							.json()
							.then((res) => {
								res.status = status;
								res.statusText = statusText;
								this.props.onRequestFailure(res);
							})
							.catch(() => {
								const message: any = { status, statusText };
								this.props.onRequestFailure(message);
							});
					}
				})
				.catch((err) => {
					this.props.onRequestFailure(err);
				});
		}
	}

	public render() {
		const { meta, t, inputPlaceholder } = this.props;

		let error: JSX.Element = null;
		if (meta) {
			error = meta.touched && meta.error && <span className="requiredfield">{meta.error}</span>;
		}

		return (
			<div className="userMentionInput">
				<MentionsInput
					value={this.state.inputValue}
					onChange={this.handleChange}
					style={this.defaultStyle}
					placeholder={
						inputPlaceholder && inputPlaceholder.length > 0 ? inputPlaceholder : t('UserMentionInput_Placeholder')
					}
					allowSpaceInQuery={true}
					singleLine={true}
				>
					<Mention
						appendSpaceOnAdd={true}
						markup="[usermention:__display__]"
						trigger="@"
						data={this.fetchUsers}
						renderSuggestion={(entry: IMentionItem, highlightedDisplay, focused) => {
							return (
								<div className={`user ${focused ? 'focused' : ''} `}>
									<div className="userDropdown">
										{entry.picture && entry.picture.length > 0 ? (
											<div style={{ display: 'inline-block' }}>
												<img {...imgSrcAttributes(entry.picture)} style={{ height: '25px', width: '25px' }} />
											</div>
										) : null}
										<div style={{ display: 'inline-block' }}>{highlightedDisplay}</div>
									</div>
								</div>
							);
						}}
						onAdd={(id, display) => {
							this.props.onMentionAdd(id, display);
						}}
						style={this.defaultMentionStyle}
					/>
				</MentionsInput>
				{error}
			</div>
		);
	}
}

export { UserMentionInput };
